import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * Confirm delete for infrastructure servers. When customers/resellers/campaigns
 * still reference the server, require picking a replacement before confirming.
 */
export default function ServerReassignDeleteDialog({
  open,
  onOpenChange,
  server,
  serverLabel = "server",
  hostText,
  alternatives = [],
  dependents = null,
  loadingDependents = false,
  busy = false,
  onConfirm,
  testIdPrefix = "server",
  extraNote,
}) {
  const [replacementId, setReplacementId] = useState("");

  useEffect(() => {
    if (!open) setReplacementId("");
  }, [open, server?.id]);

  const customers = dependents?.customers || (Array.isArray(dependents) ? dependents : []);
  const resellers = dependents?.resellers || [];
  const campaigns = dependents?.campaigns || [];
  const needsReassign = customers.length + resellers.length + campaigns.length > 0;

  const altOptions = useMemo(
    () => (alternatives || []).filter((a) => a?.id && a.id !== server?.id),
    [alternatives, server?.id],
  );

  const canSubmit = !needsReassign || !!replacementId;

  const submit = () => {
    if (!canSubmit || busy) return;
    onConfirm?.(needsReassign ? replacementId : null);
  };

  const name = server?.name || serverLabel;
  const host = hostText || server?.host || server?.public_ip || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${testIdPrefix}-confirm-delete`}>
        <DialogHeader>
          <DialogTitle>Delete {serverLabel}?</DialogTitle>
          <DialogDescription>
            This permanently deletes “{name}”{host ? ` (${host})` : ""}.
            {extraNote ? ` ${extraNote}` : ""} This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {loadingDependents && (
            <p className="text-sm text-muted-foreground" data-testid={`${testIdPrefix}-deps-loading`}>
              Checking assignments…
            </p>
          )}

          {!loadingDependents && needsReassign && (
            <div className="space-y-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid={`${testIdPrefix}-reassign-required`}>
              <p className="text-sm font-medium">
                This {serverLabel} is still assigned. Choose another {serverLabel} to move assignments to before deleting.
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 max-h-28 overflow-y-auto">
                {customers.slice(0, 8).map((c) => (
                  <li key={`c-${c.id}`}>Customer: {c.name || c.id}{c.company ? ` · ${c.company}` : ""}</li>
                ))}
                {resellers.slice(0, 8).map((r) => (
                  <li key={`r-${r.id}`}>Reseller: {r.name || r.id}{r.company ? ` · ${r.company}` : ""}</li>
                ))}
                {campaigns.slice(0, 8).map((c) => (
                  <li key={`camp-${c.id}`}>Campaign: {c.name || c.id}{c.status ? ` (${c.status})` : ""}</li>
                ))}
                {(customers.length + resellers.length + campaigns.length) > 24 && (
                  <li>…and more</li>
                )}
              </ul>
              <div className="space-y-2">
                <Label>Reassign to</Label>
                {altOptions.length === 0 ? (
                  <p className="text-sm text-destructive" data-testid={`${testIdPrefix}-no-alternatives`}>
                    No other active {serverLabel} is available. Add or activate one first.
                  </p>
                ) : (
                  <Select value={replacementId} onValueChange={setReplacementId}>
                    <SelectTrigger data-testid={`${testIdPrefix}-reassign-select`}>
                      <SelectValue placeholder={`Select replacement ${serverLabel}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {altOptions.map((a) => (
                        <SelectItem key={a.id} value={a.id} data-testid={`${testIdPrefix}-reassign-opt-${a.id}`}>
                          {a.name}{a.host || a.public_ip ? ` · ${a.host || a.public_ip}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-lg"
            onClick={() => onOpenChange?.(false)}
            data-testid={`${testIdPrefix}-confirm-cancel`}
          >
            Cancel
          </Button>
          <Button
            className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={submit}
            disabled={busy || loadingDependents || (needsReassign && (!replacementId || altOptions.length === 0))}
            data-testid={`${testIdPrefix}-confirm-ok`}
          >
            {needsReassign ? "Reassign & delete" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
